// Canvas schema — mirrors functions/src/canvas/schema.ts.
// Deliberately duplicated per CLAUDE.md (test harness, two deploy targets).

export type PhaseCode = "S1" | "S2" | "S3" | "S4" | "S5" | "S6";
export type Stage = PhaseCode | "DR" | "COMPLETE";
export type PhaseStatus = "locked" | "in_progress" | "complete";
export type DraftConfidence = "low" | "medium" | "high";

import spec from "./canvas-spec.json";

export type Criticality = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

export interface FieldDef {
  name: string;
  label: string;
  question: string;
  intent: string;
  highQualityAnswerLooksLike: string;
  code?: string;
  jsonPath?: string;
  criticality?: Criticality;
  exemplar?: string;
  companionOf?: string | null;
  requiredFor?: string;
}

export interface PhaseDef {
  code: PhaseCode;
  title: string;
  subtitle: string;
  fields: FieldDef[];
}

export const PHASES: PhaseDef[] = (spec.layers as any[]).map((l) => ({
  code: l.code as PhaseCode,
  title: l.title as string,
  subtitle: l.subtitle as string,
  fields: (l.fields as any[]).map((f) => ({
    name: f.name as string,
    label: f.label as string,
    question: f.question as string,
    intent: (f.intent ?? "") as string,
    highQualityAnswerLooksLike: (f.highQualityAnswerLooksLike ?? "") as string,
    code: f.code as string,
    jsonPath: f.jsonPath as string,
    criticality: f.criticality as Criticality,
    exemplar: (f.exemplar || undefined) as string | undefined,
    companionOf: (f.companionOf ?? null) as string | null,
    requiredFor: (f.requiredFor ?? "all") as string,
  })),
}));

export type DRCode = "DR1" | "DR2" | "DR3" | "DR4" | "DR5";
export interface DRDef { code: DRCode; question: string }
export const DR_QUESTIONS: DRDef[] = (spec.deploymentQuestions as any[]).map((d) => ({
  code: d.id as DRCode,
  question: d.question as string,
}));

// ---------------------------------------------------------------------------
// Stored state
// ---------------------------------------------------------------------------

export interface FieldAnswer {
  question: string;
  answer: string;
  score: number;
  gapFlagged: boolean;
  gapNote: string;
  confidence: "low" | "medium" | "high";
  evalText: string;
  timestamp: number;
  pushbackPass?: number;
  skipped?: boolean;
  source?: "document_upload" | "document_upload_edited";
  /** Model that produced this score — a grade is only interpretable next to it. */
  scoringModel?: string;
}

export interface PhaseState {
  status: PhaseStatus;
  qualityScore: number | null;
  fields: Record<string, FieldAnswer>;
}

export interface DRState {
  status: "locked" | "in_progress" | "complete";
  answers: Record<string, FieldAnswer>;
}

export interface ExtractedDraft {
  answer: string;
  sourceQuote: string;
  confidence: DraftConfidence;
  discardedAt?: number;
}

export interface DocUploadState {
  uploadedAt: number;
  model: string;
  sourceFile: {
    name: string;
    sizeBytes: number;
    pages?: number;
    mimeType: "application/pdf" | "text/plain" | "text/markdown";
  };
  inputTokensEstimate: number;
  drafts: Record<string, ExtractedDraft>;
}

export interface ConsistencyResult {
  code: string;
  label: string;
  verdict: "pass" | "warn" | "fail";
  detail: string;
}

export type DeploymentStage = "new_design" | "operating_system" | "post_incident";
export type Complexity = "solo_operator" | "hybrid_team" | "multi_agent";
export type BudgetMode = "normal" | "reduced" | "critical_only";

export interface CanvasSession {
  id: string;
  ownerUid: string;
  workflowName: string;
  namedOwner: string;
  industry?: string;
  deploymentStage?: DeploymentStage;
  complexity?: Complexity;
  startedAt: number;
  updatedAt: number;
  currentStage: Stage;
  currentFieldIndex: number;
  pushbackBudget: number;
  totalPushbackCount?: number;
  pushbackBudgetMode?: BudgetMode;
  phases: Record<PhaseCode, PhaseState>;
  deploymentReadiness: DRState;
  consistencyChecks: {
    status: "pending" | "complete";
    results: ConsistencyResult[];
  };
  docUpload?: DocUploadState;
  /** Millis at midnight UTC of the user-picked revision date. Cleared after the reminder cron fires. */
  nextReviewDate?: number | null;
  /** Millis. Set by the daily reminder cron when it sends. One-shot guard. */
  lastReminderSentAt?: number;
  /** Millis. Set by handleGenerateOutputs on first successful generate. */
  completionEmailSentAt?: number;
}

export const PHASE_ORDER: PhaseCode[] = ["S1", "S2", "S3", "S4", "S5", "S6"];

// ---------------------------------------------------------------------------
// Durable artifacts (mirrors functions/src/canvas/schema.ts)
// ---------------------------------------------------------------------------

export interface ArtifactRef {
  path: string;
  size: number;
  generatedAt: number;
  sha256: string;
}

export interface SignedArtifact {
  url: string;
  filename: string;
  generatedAt: number;
}

export type VariantKind = "gap-summary" | "scorecard" | "consistency" | "one-pager";

/** An AI-generated custom report: the signed download plus the request that made it. */
export interface SignedBespokeReport extends SignedArtifact {
  id: string;
  prompt: string;
}

export interface ArtifactBundle {
  baselineRecord: SignedArtifact;
  canvasJson: SignedArtifact;
  governanceReport: SignedArtifact;
  variants?: Partial<Record<VariantKind, SignedArtifact>>;
  bespoke?: SignedBespokeReport[];
}

// ---------------------------------------------------------------------------
// Canvas versioning (mirrors functions/src/canvas/schema.ts)
// ---------------------------------------------------------------------------

export interface VersionSummary {
  version: number;
  createdAt: number;
  parentVersion: number | null;
  reason: "phase9" | "phase10-review";
  grade: string;
  composite: number;
}

export interface CanvasVersionDoc {
  version: number;
  createdAt: number;
  parentVersion: number | null;
  reason: "phase9" | "phase10-review";
  grade: string;
  composite: number;
  // Opaque payload; the snapshot viewer does not render phase internals.
  phases: Record<string, unknown>;
  deploymentReadiness: { status: string; answers: Record<string, unknown> };
  consistencyChecks: {
    status: string;
    results: Array<{ code: string; label: string; verdict: string; detail: string }>;
  };
  artifacts: ArtifactBundle;
}

// ---------------------------------------------------------------------------
// Review (Track 5.3 / Phase 10) — mirrors functions/src/canvas/schema.ts
// ---------------------------------------------------------------------------

// A review walks the six lettered phases and then the deployment-readiness step.
export type ReviewLayer = PhaseCode | "DR";

export interface ReviewState {
  status: "in_progress" | "complete";
  startedAt: number;
  parentVersion: number;
  currentLayer: ReviewLayer;
  draftPhases: Record<PhaseCode, PhaseState>;
  draftDR?: DRState;
}

export interface ReviewFieldDelta {
  phase: ReviewLayer;
  field: string;
  fieldLabel: string;
  previousAnswer: string;
  newAnswer: string;
  previousScore: number;
  newScore: number;
  previousGapFlagged: boolean;
  newGapFlagged: boolean;
  previousGapNote: string;
  newGapNote: string;
}

export interface ReviewConsistencyDelta {
  code: string;
  label: string;
  previousVerdict: "pass" | "warn" | "fail" | "absent";
  newVerdict: "pass" | "warn" | "fail" | "absent";
}

export interface ReviewDelta {
  fieldsChanged: ReviewFieldDelta[];
  consistencyDelta: ReviewConsistencyDelta[];
  gradeDelta: {
    previous: string;
    new: string;
    previousComposite: number;
    newComposite: number;
  };
  layersEdited: ReviewLayer[];
}

export function findPhase(code: PhaseCode): PhaseDef {
  const p = PHASES.find((x) => x.code === code);
  if (!p) throw new Error(`Unknown phase: ${code}`);
  return p;
}

export function findField(code: PhaseCode, idx: number): FieldDef {
  const f = findPhase(code).fields[idx];
  if (!f) throw new Error(`No field at ${code}[${idx}]`);
  return f;
}

export function totalFields(): number {
  return PHASES.reduce((sum, p) => sum + p.fields.length, 0);
}

export function fieldsAnswered(session: CanvasSession): number {
  let n = 0;
  for (const p of PHASE_ORDER) {
    n += Object.keys(session.phases[p].fields).length;
  }
  return n;
}

// ---------------------------------------------------------------------------
// Coach chat (Track 5.5) — mirrors functions/src/canvas/schema.ts.
// ---------------------------------------------------------------------------

export type CoachMessageRole = "user" | "assistant";

export interface CoachThread {
  id: string;
  ownerUid: string;
  title: string;
  snapshotVersion: number;
  createdAt: number;
  updatedAt: number;
  messageCount: number;
}

export interface CoachMessage {
  id: string;
  role: CoachMessageRole;
  content: string;
  createdAt: number;
  modelUsed?: { provider: string; model: string };
}
